/* eslint-disable jsx-a11y/label-has-associated-control */
import * as Slider from '@radix-ui/react-slider';
import { useState } from 'react';

import { START_OF_DAY } from '@/lib/constants';

// Helper functions for time conversion
const minutesToTimeString = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const timeStringToMinutes = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

// Snapping function - snaps to nearest hour except for 23:59
const snapToNearestHour = (minutes: number): number => {
  // If it's 23:59 (1439 minutes), keep it as is
  if (minutes === 1439) return 1439;

  // Otherwise snap to nearest hour
  const hours = Math.round(minutes / 60);
  return Math.min(hours * 60, 1439); // Ensure we don't exceed 23:59
};

interface TimeRangeProps {
  defaultTime?: string; // e.g., "09:00"
  onTimeChange?: (time: string) => void;
}

const TimeRange = ({ defaultTime = START_OF_DAY, onTimeChange }: TimeRangeProps) => {
  const [time, setTime] = useState<number>(timeStringToMinutes(defaultTime));

  const handleValueChange = (values: number[]) => {
    // Snap to the nearest hour rather than each minute
    const snappedValue = snapToNearestHour(values[0]);

    setTime(snappedValue);

    if (onTimeChange) {
      onTimeChange(minutesToTimeString(snappedValue));
    }
  };

  return (
    <div className="mx-auto w-full max-w-md">
      <div className="mb-3 font-mono text-md text-grey-800">{minutesToTimeString(time)}</div>

      <Slider.Root
        className="relative flex h-5 w-full touch-none select-none items-center"
        value={[time]}
        onValueChange={handleValueChange}
        max={1439} // 23:59 in minutes
        min={0} // 00:00 in minutes
        step={1} // 1 minute steps for smooth dragging
      >
        <Slider.Track className="relative h-2 grow rounded-full bg-grey-200">
          <Slider.Range className="absolute h-full rounded-full bg-lightBlue-500" />
        </Slider.Track>
        <Slider.Thumb
          className="block h-5 w-5 rounded-full border-2 border-lightBlue-500 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-lightBlue-500 focus:ring-offset-2"
          aria-label="Selected time"
        />
      </Slider.Root>

      <div className="mt-1 flex justify-between text-[10px] text-grey-400">
        <span>00:00</span>
        <span>06:00</span>
        <span>12:00</span>
        <span>18:00</span>
        <span>23:59</span>
      </div>
    </div>
  );
};

export { TimeRange };
